import React from 'react';
import {ToastContainer} from 'react-toastify';
import {SYSTEM_ANNOUNCEMENTS_CONTAINER_ID} from './constants';
import {
    SYSTEM_ANNOUNCEMENTS_OFFSET_VAR,
    useSystemAnnouncementsStackHeight,
} from './useSystemAnnouncementsStackHeight';

/**
 * Both app toast containers. The system-announcements container (stacked,
 * persistent) sits at the default top-center position; the transient default
 * container is pushed below it by the measured stack height via the
 * --system-announcements-offset CSS variable (0px without announcements, so
 * transient toasts render exactly like before).
 *
 * IMPORTANT: the containers must never re-render — react-toastify v11 leaves
 * mounted toasts with a stale close handler when a ToastContainer re-renders
 * with changed props (close clicks are silently swallowed). Hence: memoized
 * component, module-level style objects, and the offset flows through the CSS
 * variable instead of React state.
 *
 * The default container must NOT get a containerId: react-toastify registers
 * it under the internal default id, which is what all existing
 * toast.success/error(...) calls without an explicit containerId target.
 */
const SYSTEM_CONTAINER_STYLE = {
    '--toastify-toast-width': 'min(480px, calc(100vw - 32px))',
} as React.CSSProperties;

const TRANSIENT_CONTAINER_STYLE = {
    top: `calc(var(--toastify-toast-top) + var(${SYSTEM_ANNOUNCEMENTS_OFFSET_VAR}, 0px))`,
    transition: 'top 0.3s ease',
} as React.CSSProperties;

const AppToastContainers = React.memo(() => {
    useSystemAnnouncementsStackHeight();

    return (
        <>
            <ToastContainer
                containerId={SYSTEM_ANNOUNCEMENTS_CONTAINER_ID}
                theme={"light"}
                position={"top-center"}
                stacked
                autoClose={false}
                closeOnClick={false}
                draggable={false}
                hideProgressBar
                style={SYSTEM_CONTAINER_STYLE}
            />
            <ToastContainer
                theme={"light"}
                position={"top-center"}
                hideProgressBar={false}
                newestOnTop={false}
                style={TRANSIENT_CONTAINER_STYLE}
            />
        </>
    );
});

export default AppToastContainers;
